import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
} from "@mui/material";
import SeoHead from "../components/SeoHead";
import { getPublicSeo } from "../utils/seoCatalog";
import {
  CompareArrows,
  SportsBaseball,
  SportsBasketball,
  SportsFootball,
  Groups,
  QueryStats,
  ShowChart,
  Timeline,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { useAnalytics } from "../hooks/useAnalytics";
import LeaguePhotoRow from "../components/marketing/LeaguePhotoRow";
import MarketingCommunitySection from "../components/marketing/MarketingCommunitySection";
import MarketingLineupSection from "../components/marketing/MarketingLineupSection";
import MarketingReveal, { revealDelay } from "../components/marketing/MarketingReveal";
import MarketingTriviaSection from "../components/marketing/MarketingTriviaSection";
import MatchupCards from "../components/marketing/MatchupCards";
import MarketingHeroBackground from "../components/marketing/MarketingHeroBackground";
import MarketingCtaBackground, {
  MarketingVsBadge,
} from "../components/marketing/MarketingCtaBackground";
import {
  accentColors,
  featureIconBox,
  socialProofIconBox,
} from "../components/marketing/marketingStyles";
import { colors } from "../theme";
import { brandFilledButtonSx } from "../utils/brandAssets";

const heroOutlinedButtonSx = {
  px: 4,
  py: 1.5,
  fontSize: "1.05rem",
  fontWeight: 600,
  borderColor: colors.border,
  "&:hover": {
    borderColor: "text.secondary",
    bgcolor: "action.hover",
  },
};

const SOCIAL_PROOF = [
  {
    value: "20,000+",
    label: "Players",
    icon: SportsBaseball,
    accent: accentColors[0],
  },
  {
    value: "125",
    label: "Years of History",
    icon: SportsBasketball,
    accent: accentColors[1],
  },
  {
    value: "∞",
    label: "Debates to Settle",
    icon: SportsFootball,
    accent: accentColors[2],
  },
];

const MarketingPage = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const { trackCustomEvent } = useAnalytics();

  React.useEffect(() => {
    trackCustomEvent("marketing_page_viewed", {
      page: "marketing_home",
      source: "direct_visit",
      user_status: isAuthenticated ? "authenticated" : "unauthenticated",
    });
  }, [trackCustomEvent, isAuthenticated]);

  if (isAuthenticated && !loading) {
    return <Navigate to="/app" replace />;
  }

  const features = [
    {
      icon: SportsBaseball,
      title: "Find Any Legend",
      description:
        "Track down every stat on the players fueling your barstool arguments — baseball, basketball, and football, from dead-ball icons to today's stars.",
      accent: accentColors[0],
    },
    {
      icon: CompareArrows,
      title: "Settle It Side-by-Side",
      description:
        "Put Ruth against Bonds, Jordan against LeBron, Brady against Manning. Head-to-head comparisons that turn opinions into evidence.",
      accent: accentColors[1],
    },
    {
      icon: ShowChart,
      title: "Dig Into the Numbers",
      description:
        "Career arcs, peak seasons, advanced metrics — interactive charts that reveal what the box score misses.",
      accent: accentColors[2],
    },
    {
      icon: Timeline,
      title: "Track the Trends",
      description:
        "Watch careers unfold season by season. Spot the hot streaks, the decline phases, the comeback stories.",
      accent: accentColors[3],
    },
    {
      icon: Groups,
      title: "Team Deep Dives",
      description:
        "Franchise histories, roster breakdowns, and the stats behind every dynasty and drought.",
      accent: accentColors[4],
    },
    {
      icon: QueryStats,
      title: "Build Your Query",
      description:
        "Custom filters to find exactly who you're looking for — lefty sluggers, 50-point nights, 4,000-yard seasons, the unicorns.",
      accent: accentColors[5],
    },
  ];

  const handleGetStarted = (location = "hero_section") => {
    trackCustomEvent("marketing_cta_clicked", {
      cta_type: "get_started",
      location,
      source: "marketing_page",
    });
    navigate("/signup");
  };

  const handleMatchupClick = (matchup, index) => {
    trackCustomEvent("marketing_matchup_clicked", {
      matchup: `${matchup.playerA}_vs_${matchup.playerB}`,
      matchup_index: index,
      source: "marketing_page",
    });
    handleGetStarted("matchup_card");
  };

  return (
    <>
      <SeoHead {...getPublicSeo("home")} />

      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        {/* Hero */}
        <MarketingHeroBackground sx={{ py: { xs: 8, md: 12 } }}>
          <Container maxWidth="lg">
            <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
              <Grid item xs={12} md={6}>
                <Chip
                  label="MLB, NBA, and NFL stats"
                  size="small"
                  sx={{
                    mb: 2,
                    bgcolor: `${accentColors[0]}18`,
                    color: accentColors[0],
                    fontWeight: 600,
                    border: `1px solid ${accentColors[0]}33`,
                  }}
                />
                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    mb: 2,
                    fontSize: { xs: "2.75rem", sm: "3.25rem", md: "3.75rem" },
                    lineHeight: 1.1,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Settle the{" "}
                  <Box
                    component="span"
                    sx={{
                      background: `linear-gradient(90deg, ${accentColors[0]}, ${accentColors[1]})`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    debate.
                  </Box>
                </Typography>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{
                    mb: 4,
                    fontWeight: 400,
                    lineHeight: 1.7,
                    maxWidth: 520,
                    fontSize: { xs: "1rem", md: "1.15rem" },
                  }}
                >
                  Compare legends, build lineups, play daily trivia, and vote in
                  community debates — backed by real MLB, NBA, and NFL stats,
                  not hot takes.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => handleGetStarted("hero_section")}
                    sx={{
                      ...brandFilledButtonSx,
                      px: 4,
                      py: 1.5,
                      fontSize: "1.05rem",
                      fontWeight: 700,
                    }}
                  >
                    Start Comparing
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="large"
                    onClick={() => handleGetStarted("hero_trivia")}
                    sx={heroOutlinedButtonSx}
                  >
                    Play Trivia
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="large"
                    onClick={() => {
                      trackCustomEvent("marketing_cta_clicked", {
                        cta_type: "learn_more",
                        location: "hero_section",
                        destination: "about_page",
                        source: "marketing_page",
                      });
                      navigate("/about");
                    }}
                    sx={heroOutlinedButtonSx}
                  >
                    Our Story
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <MatchupCards onCardClick={handleMatchupClick} />
              </Grid>
            </Grid>
          </Container>
        </MarketingHeroBackground>

        {/* Across the leagues */}
        <Box
          sx={{
            py: { xs: 5, md: 6 },
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        >
          <Container maxWidth="lg">
            <MarketingReveal>
              <LeaguePhotoRow />
            </MarketingReveal>
          </Container>
        </Box>

        {/* Social Proof */}
        <Box
          sx={{
            py: { xs: 4, md: 5 },
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={3} justifyContent="center">
              {SOCIAL_PROOF.map((stat, index) => {
                const StatIcon = stat.icon;
                return (
                <Grid item xs={12} sm={4} key={stat.label}>
                  <MarketingReveal delay={revealDelay(index, 70)}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: { xs: "flex-start", sm: "center" },
                      gap: 2,
                      py: 1,
                      px: { xs: 2, sm: 0 },
                    }}
                  >
                    <Box sx={socialProofIconBox(stat.accent)}>
                      <StatIcon />
                    </Box>
                    <Box>
                      <Typography
                        variant="h4"
                        fontWeight={800}
                        sx={{ color: stat.accent, lineHeight: 1.1 }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        {stat.label}
                      </Typography>
                    </Box>
                  </Box>
                  </MarketingReveal>
                </Grid>
              );
              })}
            </Grid>
          </Container>
        </Box>

        {/* Features */}
        <Container
          maxWidth="lg"
          sx={{ py: { xs: 7, md: 10 } }}
          id="features-section"
        >
          <MarketingReveal>
          <Box textAlign="center" sx={{ mb: 6 }}>
            <Chip
              label="Built for fans who love the numbers"
              size="small"
              sx={{
                mb: 2,
                bgcolor: "action.hover",
                color: "text.secondary",
                fontWeight: 500,
              }}
            />
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: "1.85rem", md: "2.5rem" },
              }}
            >
              Everything you need to win the argument
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 560, mx: "auto", fontWeight: 400, lineHeight: 1.6 }}
            >
              Search, compare, visualize, and query — then take the numbers
              into lineups, community debates, and daily trivia.
            </Typography>
          </Box>
          </MarketingReveal>

          <Grid container spacing={3}>
            {features.map((feature, index) => {
              const FeatureIcon = feature.icon;
              return (
              <Grid item xs={12} sm={6} md={4} key={feature.title}>
                <MarketingReveal delay={revealDelay(index, 80)}>
                <Card
                  onClick={() => {
                    trackCustomEvent("marketing_feature_interaction", {
                      feature_title: feature.title,
                      feature_index: index,
                      interaction_type: "card_click",
                      source: "marketing_page",
                    });
                  }}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease",
                    cursor: "pointer",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 2,
                      bgcolor: feature.accent,
                      opacity: 0,
                      transition: "opacity 0.25s ease",
                    },
                    "&:hover": {
                      transform: "translateY(-6px)",
                      borderColor: `${feature.accent}55`,
                      boxShadow: `0 12px 40px ${feature.accent}15`,
                      "&::before": { opacity: 1 },
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, py: 3.5, px: 3 }}>
                    <Box sx={featureIconBox(feature.accent)}>
                      <FeatureIcon />
                    </Box>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ mb: 1.5, fontWeight: 700 }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7 }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
                </MarketingReveal>
              </Grid>
            );
            })}
          </Grid>
        </Container>

        <MarketingLineupSection
          onStart={() => handleGetStarted("lineup_section")}
        />

        <MarketingCommunitySection
          onStart={() => handleGetStarted("community_section")}
        />

        <MarketingTriviaSection
          onPlay={() => handleGetStarted("trivia_section")}
        />

        {/* CTA */}
        <MarketingCtaBackground>
          <Container maxWidth="md">
            <MarketingReveal>
            <Box textAlign="center">
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: "1.75rem", md: "2.25rem" },
                  textShadow: "0 2px 16px rgba(0, 0, 0, 0.75)",
                }}
              >
                Ready to pick a side?
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: 2.5,
                  maxWidth: 480,
                  mx: "auto",
                  fontWeight: 400,
                  lineHeight: 1.6,
                  color: "rgba(228, 228, 231, 0.92)",
                  textShadow: "0 1px 10px rgba(0, 0, 0, 0.65)",
                }}
              >
                Join fans who build lineups, play trivia, and settle debates
                with the record — baseball, basketball, and football.
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", my: 2.5 }}>
                <MarketingVsBadge />
              </Box>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  trackCustomEvent("marketing_cta_clicked", {
                    cta_type: "start_exploring",
                    location: "cta_section",
                    source: "marketing_page",
                  });
                  handleGetStarted("cta_section");
                }}
                sx={{
                  ...brandFilledButtonSx,
                  px: 6,
                  py: 2,
                  fontSize: "1.1rem",
                  fontWeight: 700,
                }}
              >
                Start Exploring Free
              </Button>
            </Box>
            </MarketingReveal>
          </Container>
        </MarketingCtaBackground>

        {/* Footer */}
        <Box
          sx={{
            bgcolor: "background.default",
            py: 4,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                © 2026 Stats.Fan. All rights reserved.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Created by James Carvallyo
              </Typography>
            </Box>
          </Container>
        </Box>

      </Box>
    </>
  );
};

export default MarketingPage;
